import React, { useState, useEffect } from 'react';
import { Button, Modal, Input, Form, message, Checkbox, Select, Tabs, Drawer, Pagination } from 'antd';
import dayjs from "dayjs";
import { CloseOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Sidebar from '../Components/SideBar';
import { buildApiUrl } from '../utils/api';

const { Option } = Select;
const User = () => {
    const [users, setUsers] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [assignForm] = Form.useForm();
    const [deletingUserId, setDeletingUserId] = useState(null);
    const token = localStorage.getItem("token");
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const { TabPane } = Tabs;
    const [lastRefreshed, setLastRefreshed] = useState("");

    useEffect(() => {
        const now = new Date();
        const formatted = dayjs(now).format("DD MMM YYYY, hh:mm A");
        setLastRefreshed(formatted);
    }, []);

    const [adminPage, setAdminPage] = useState(1);
    const [userPage, setUserPage] = useState(1);
    const [teamPage, setTeamPage] = useState(1);

    const pageSize = 5;

    const adminUsers = users.filter(user => user.role === 'admin');
    const normalUsers = users.filter(user => user.role === 'user');
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await fetch(buildApiUrl('/api/users/users'), {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                setUsers(data);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        fetchUsers();
    }, [token]);

    const [logs, setLogs] = useState([]);
    const [error, setError] = useState(null);
    useEffect(() => {
        const fetchLogs = async () => {
            setLoading(true);
            try {
                const response = await fetch(buildApiUrl('/api/daily-logs'), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch logs: ${response.statusText}`);
                }

                const result = await response.json();
                console.log("Fetched Logs:", result);


                const directNames = result.dailyLogs?.flatMap(log =>
                    log.personnel
                        ?.filter(person => person.isDirectName)
                        .map(person => person.username)
                ) || [];


                const uniqueDirectNames = [...new Set(directNames)];

                setLogs(uniqueDirectNames);
            } catch (error) {
                console.error("Error fetching logs:", error);
                setError("Failed to load logs. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
    }, [token]);


    const showModal = () => {
        setIsModalVisible(true);
    };


    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();

    };


    const handleCreateUser = async (values) => {
        setIsSubmitting(true);
        const newUser = {
            username: values.username,
            email: values.email,
            password: values.password,
            role: values.role.toLowerCase(),
        };

        try {
            const response = await fetch(buildApiUrl('/api/users/register'), {
                method: 'POST',
                headers: {
                    "Authorization": `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newUser),
            });

            if (response.ok) {
                const createdUser = await response.json();
                setUsers((prevUsers) => [...prevUsers, createdUser]);

                handleCancel();
                window.location.reload();
                message.success('User created successfully!');
            } else {
                message.error('Failed to create user.');
                console.error('Error creating user:', response.statusText);
            }
        } catch (error) {
            message.error('Error creating user.');
            console.error('Error creating user:', error);
        }
        setIsSubmitting(false);
    };

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await fetch(
                    buildApiUrl('/api/daily-logs/projects'),
                    {
                        method: "GET",
                        headers: {
                            "Authorization": `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                const data = await response.json();
                if (data.projects) {
                    setProjects(data.projects);
                }
            } catch (error) {
                console.error("Error fetching projects:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, [token]);

    const showAssignModal = (userId) => {
        setSelectedUserId(userId);
        setIsAssignModalVisible(true);
    };

    const handleAssignTasks = async (values) => {
        if (!selectedUserId) {
            message.error("Please select a user before assigning projects.");
            return;
        }

        setIsSubmitting(true);

        const requestData = {
            project: Array.isArray(values.projects) ? values.projects : [values.projects],
            user: selectedUserId,
        };

        console.log("Data being sent to backend:", JSON.stringify(requestData, null, 2));

        try {
            const response = await fetch(buildApiUrl('/api/task'), {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestData),
            });

            const result = await response.json();
            console.log("API Response:", result);

            if (response.ok) {
                message.success("Projects assigned successfully!");
                assignForm.resetFields();
                setIsAssignModalVisible(false);
            } else {
                message.error(result.message || "Failed to assign projects.");
            }
        } catch (error) {
            console.error("Error assigning projects:", error);
            message.error("Error assigning projects. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };





    const handleDeleteUser = async (userId, userName) => {
        setDeletingUserId(userId);
        const checkboxState = { deleteLogs: false };

        Modal.confirm({
            title: "Confirm User Deletion",
            content: (
                <div>
                    <p>Are you sure you want to delete {userName}?</p>
                    <Checkbox onChange={(e) => (checkboxState.deleteLogs = e.target.checked)}>
                        Also delete Daily Logs
                    </Checkbox>
                </div>
            ),
            onOk: async () => {
                if (!checkboxState.deleteLogs) {
                    message.warning("Please select the checkbox to proceed with deletion.");
                    return;
                }

                try {
                    const response = await fetch(
                        buildApiUrl(`/api/users/deleteuser?id=${userId}`),
                        {
                            method: 'DELETE',
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Accept': 'application/json',
                            },
                        }
                    );

                    if (response.ok) {
                        message.success(`User ${userName} deleted successfully!`);
                        setUsers(users.filter(user => user._id !== userId));
                    } else {
                        message.error('Failed to delete user');
                    }
                } catch (error) {
                    message.error('Error deleting user');
                    console.error('Error deleting user:', error);
                }
            },
        });
    };

    const handleAssignCancel = () => {
        setIsAssignModalVisible(false);
        assignForm.resetFields();
    };



    return (
        <div className='flex flex-row'>
            <Sidebar />
            <div className='w-full'>
                <div className=" flex justify-end pt-10 px-8">
                    <p className="text-sm text-gray-500">Last refreshed {lastRefreshed}</p>
                </div>
                <div className="w-full px-8 flex justify-between items-center ">
                    <h2 className="text-2xl font-semibold text-[#0D2B52]">Users</h2>
                    <Button
                        className="border border-gray-300 text-[#0D2B52]"
                        onClick={showModal}
                    >
                        <PlusOutlined /> Add User
                    </Button>
                </div>
                <div className="overflow-x-auto scrollbar-hide">
                    <Tabs defaultActiveKey="1" className=" custom-tabs bg-white p-6 rounded-xl shadow-sm w-full">
                        {/* Admin Tab */}
                        <TabPane tab="Admins" key="1">
                            <div className="">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="p-3 text-left font-medium text-gray-600">Username</th>
                                            <th className="p-3 text-left font-medium text-gray-600">Email</th>
                                            <th className="p-3 text-left font-medium text-gray-600">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="font-semibold">
                                        {adminUsers.length > 0 ? (
                                            adminUsers
                                                .slice((adminPage - 1) * pageSize, adminPage * pageSize)
                                                .map(user => (
                                                    <tr key={user._id} className="hover:bg-gray-50 border-t">
                                                        <td className="p-3">{user.username}</td>
                                                        <td className="p-3">{user.email}</td>
                                                        <td className="p-3">
                                                            <Button
                                                                type="primary"
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                                onClick={() => handleDeleteUser(user._id, user.username)}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                        ) : (
                                            <tr>
                                                <td colSpan="3" className="p-4 text-center text-gray-500">
                                                    No Admins found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                {adminUsers.length > pageSize && (
                                    <div className="mt-4 flex justify-end">
                                        <Pagination
                                            current={adminPage}
                                            pageSize={pageSize}
                                            total={adminUsers.length}
                                            onChange={(page) => setAdminPage(page)}
                                        />
                                    </div>
                                )}
                            </div>
                        </TabPane>

                        {/* User Tab */}
                        <TabPane tab="Users" key="2">
                            <div className="">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="p-3 text-left font-medium text-gray-600">Username</th>
                                            <th className="p-3 text-left font-medium text-gray-600">Email</th>
                                            <th className="p-3 text-left font-medium text-gray-600">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="font-semibold">
                                        {normalUsers.length > 0 ? (
                                            normalUsers
                                                .slice((userPage - 1) * pageSize, userPage * pageSize)
                                                .map(user => (
                                                    <tr key={user._id} className="hover:bg-gray-50 border-t">
                                                        <td className="p-3">{user.username}</td>
                                                        <td className="p-3">{user.email}</td>
                                                        <td className="p-3">
                                                            <div className="flex space-x-2">
                                                                <Button
                                                                    type="default"
                                                                    onClick={() => showAssignModal(user._id)}
                                                                >
                                                                    Assign Project
                                                                </Button>
                                                                <Button
                                                                    type="primary"
                                                                    danger
                                                                    icon={<DeleteOutlined />}
                                                                    onClick={() => handleDeleteUser(user._id, user.username)}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                        ) : (
                                            <tr>
                                                <td colSpan="3" className="p-4 text-center text-gray-500">
                                                    No Users found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                {normalUsers.length > pageSize && (
                                    <div className="mt-4 flex justify-end">
                                        <Pagination
                                            current={userPage}
                                            pageSize={pageSize}
                                            total={normalUsers.length}
                                            onChange={(page) => setUserPage(page)}
                                        />
                                    </div>
                                )}
                            </div>
                        </TabPane>

                        {/* Site Team Tab */}
                        <TabPane tab="Site Team" key="3">
                            <div className="">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="p-3 text-left font-medium text-gray-600">Username</th>
                                        </tr>
                                    </thead>
                                    <tbody className="font-semibold text-sm">
                                        {logs.length > 0 ? (
                                            logs
                                                .slice((teamPage - 1) * pageSize, teamPage * pageSize)
                                                .map((username, index) => (
                                                    <tr key={index} className="hover:bg-gray-50 border-t text-sm">
                                                        <td className="p-3">{username}</td>
                                                    </tr>
                                                ))
                                        ) : (
                                            <tr>
                                                <td className="p-4 text-center text-gray-500 text-sm">No Users found.</td>
                                            </tr>
                                        )}
                                    </tbody>

                                </table>

                                {logs.length > pageSize && (
                                    <div className="mt-4 flex justify-end">
                                        <Pagination
                                            current={teamPage}
                                            pageSize={pageSize}
                                            total={logs.length}
                                            onChange={(page) => setTeamPage(page)}
                                        />
                                    </div>
                                )}
                            </div>
                        </TabPane>
                    </Tabs>
                </div>
            </div>

            <Drawer
                title={
                    <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold">Add User</span>
                        <CloseOutlined
                            className="text-lg cursor-pointer hover:text-red-500"
                            onClick={handleCancel}
                        />
                    </div>
                }
                placement="right"
                width={400}
                onClose={handleCancel}
                open={isModalVisible}
                closable={false}
                footer={
                    <div className="text-center w-[100%] px-6 py-4  bg-white">
                        <Button
                            className="w-full !bg-[#0D2B52] hover:!bg-[#173E73] !text-white text-base h-10 rounded-[8px]"
                            htmlType="submit"
                            loading={isSubmitting}
                            onClick={() => form.submit()}
                        >
                            Create User
                        </Button>
                    </div>
                }
            >
                <Form
                    form={form}
                    onFinish={handleCreateUser}
                    layout="vertical"
                    initialValues={{
                        role: 'user',
                    }}
                >
                    <Form.Item
                        label="Username"
                        name="username"
                        required={false}
                        rules={[{ required: true, message: 'Please input the username!' }]}
                    >
                        <Input placeholder="Enter Username  " />
                    </Form.Item>

                    <Form.Item
                        label="Email"
                        name="email"
                        required={false}
                        rules={[{ required: true, type: 'email', message: 'Please input a valid email!' }]}
                    >
                        <Input placeholder="Project name" />
                    </Form.Item>

                    <Form.Item
                        label="Password"
                        name="password"
                        required={false}
                        rules={[{ required: true, message: 'Please input the password!' }]}
                    >
                        <Input placeholder="Enter detailed log description" />
                    </Form.Item>

                    <Form.Item
                        label="Role"
                        name="role"
                        required={false}
                        rules={[{ required: true, message: 'Please select a role!' }]}
                    >
                        <select
                            value={form.getFieldValue("role")}
                            onChange={(e) => form.setFieldsValue({ role: e.target.value })}
                            className="w-full border border-gray-300 rounded p-2"
                        >
                            <option value="">Dropdown option</option>
                            <option value="user">user</option>
                            <option value="admin">admin</option>
                        </select>
                    </Form.Item>
                </Form>
            </Drawer>


            <Modal
                title="Assign Project"
                visible={isAssignModalVisible}
                onCancel={handleAssignCancel}
                footer={null}
            >
                <Form form={assignForm} onFinish={handleAssignTasks} layout="vertical">
                    <Form.Item
                        label="Select Projects"
                        name="projects"
                        rules={[{ required: true, message: "Please select at least one project!" }]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select projects"
                            style={{ width: "100%" }}
                            loading={loading}
                        >
                            {projects && projects.length > 0 ? (
                                projects.map((project) => (
                                    <Option key={project._id} value={project._id} style={{ color: "black" }}>
                                        {project.projectName}
                                    </Option>
                                ))
                            ) : (
                                <Option disabled>No projects available</Option>
                            )}
                        </Select>
                    </Form.Item>


                    <div className="flex justify-end">
                        <Button type="default" onClick={handleAssignCancel} className="mr-3">
                            Cancel
                        </Button>

                        <Button type="primary" htmlType="submit" loading={isSubmitting}>
                            Assign
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};



export default User;
